/**
 * Certification expiry status.
 *
 * `cert.expires` was captured and then printed as flat grey text, so a lapsed
 * professional licence looked exactly like a current one. For a firm that signs
 * and seals drawings that is a live business risk, not a styling detail.
 */

export const EXPIRING_SOON_DAYS = 90;

export const CERT_STATUS = {
  CURRENT: 'current',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
  UNKNOWN: 'unknown',
};

/**
 * Classify a certification by its expiry date.
 * `now` is injectable so this is testable without freezing the clock.
 */
// A bare "YYYY-MM-DD" is parsed by Date as UTC midnight, so anywhere west of
// UTC it lands on the previous local day — every licence would appear to expire
// a day early. Expiry dates are calendar dates, not instants, so parse them in
// local time.
function parseCalendarDate(value) {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
}

export function certStatus(expires, now = new Date()) {
  if (!expires) return { status: CERT_STATUS.UNKNOWN, days: null };

  const end = parseCalendarDate(expires);
  if (Number.isNaN(end.getTime())) return { status: CERT_STATUS.UNKNOWN, days: null };

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  // Compare whole days, so a licence expiring later today is not already lapsed
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfExpiry = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const days = Math.round((endOfExpiry - startOfToday) / MS_PER_DAY);

  if (days < 0) return { status: CERT_STATUS.EXPIRED, days };
  if (days <= EXPIRING_SOON_DAYS) return { status: CERT_STATUS.EXPIRING, days };
  return { status: CERT_STATUS.CURRENT, days };
}

/** Short human label for the badge beside a certification. */
export function certStatusLabel({ status, days }) {
  switch (status) {
    case CERT_STATUS.EXPIRED:
      return days === -1 ? 'Expired yesterday' : `Expired ${Math.abs(days)} days ago`;
    case CERT_STATUS.EXPIRING:
      if (days === 0) return 'Expires today';
      if (days === 1) return 'Expires tomorrow';
      return `Expires in ${days} days`;
    case CERT_STATUS.CURRENT:
      return 'Current';
    default:
      return null;
  }
}
